import { forwardRef, useCallback, useState } from 'react';

import { AlertTriangle } from '@crehana/compass.icons.outline/v2/alert-triangle';
import { Refresh } from '@crehana/compass.icons.outline/v2/refresh';
import { Button } from '@crehana/compass.ui/button';
import { ComplexDialog } from '@crehana/compass.ui/modals/dialogs/complex-dialog';
import { toast } from '@crehana/compass.ui/toaster';

import { useHomeTranslations } from '../../hooks/use-home-translations';
import { EmptyState } from './components/empty-state';
import { LoadingState } from './components/loading-state';
import { NextPaymentDate } from './components/next-payment-date';
import { PayrollReceiptsList } from './components/payroll-receipts-list';
import { ViewReceiptModal } from './components/view-receipt-modal/view-receipt-modal';
import { useNextPaymentDate } from './hooks/use-next-payment-date';
import { usePayrollReceipts } from './hooks/use-payroll-receipts';
import type { PayrollReceipt } from './types';
import { downloadReceiptPdf } from './utils/download-receipt-pdf';

export const PayrollReceiptWidget = forwardRef<
  HTMLDivElement,
  Record<string, never>
>(function PayrollReceiptWidget(_, ref) {
  const { t } = useHomeTranslations();
  const {
    data: receipts,
    isLoading: receiptsLoading,
    error: receiptsError,
    refetch: refetchReceipts,
  } = usePayrollReceipts();
  const {
    data: nextPayment,
    isLoading: nextPaymentLoading,
    refetch: refetchNextPayment,
  } = useNextPaymentDate();

  const [downloadingReceiptId, setDownloadingReceiptId] = useState<
    string | null
  >(null);
  const [receiptToView, setReceiptToView] = useState<PayrollReceipt | null>(
    null,
  );

  const isLoading = receiptsLoading || nextPaymentLoading;
  const hasError = receiptsError != null;
  const hasReceipts = Array.isArray(receipts) && receipts.length > 0;

  const handleRetry = useCallback(() => {
    refetchReceipts();
    refetchNextPayment();
  }, [refetchReceipts, refetchNextPayment]);

  const handleCloseViewModal = useCallback(() => {
    setReceiptToView(null);
  }, []);

  const handleDownloadReceipt = useCallback(
    async (receipt: PayrollReceipt) => {
      setDownloadingReceiptId(receipt.id);
      try {
        await downloadReceiptPdf(receipt);
        toast.success(t('PAYROLL_TOAST_DOWNLOAD_SUCCESS'), { duration: 4000 });
      } catch {
        toast.error(t('PAYROLL_TOAST_DOWNLOAD_ERROR'), {
          description: t('PAYROLL_TOAST_DOWNLOAD_ERROR_DESCRIPTION'),
        });
      } finally {
        setDownloadingReceiptId(null);
      }
    },
    [t],
  );

  const handleViewReceipt = useCallback((receipt: PayrollReceipt) => {
    setReceiptToView(receipt);
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="tw-cds-mt-space-04">
          <LoadingState />
        </div>
      );
    }
    if (hasError) {
      return (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-cds-gap-content-elements">
          <span className="tw-rounded-full tw-cds-w-56 tw-cds-h-56 tw-flex tw-items-center tw-justify-center tw-bg-[var(--cds-icon-bg-alert)]">
            <AlertTriangle
              className="tw-text-[var(--cds-icon-color-alert)]"
              size={24}
            />
          </span>

          <div>
            <p className="tw-cds-font-subtitle-01 tw-text-center">
              {t('PAYROLL_ERROR_LOAD_TITLE')}
            </p>
            <p className="tw-cds-font-body-01 tw-text-center">
              {t('PAYROLL_ERROR_LOAD_DESCRIPTION')}
            </p>
          </div>

          <Button type="button" size="sm" onClick={handleRetry}>
            <Refresh size={20} className="tw-text-inherit tw-fill-current" />
            {t('PAYROLL_RETRY')}
          </Button>
        </div>
      );
    }

    if (!hasReceipts) {
      return <EmptyState />;
    }

    return (
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-space-04 tw-min-h-0 tw-overflow-auto">
        {nextPayment && (
          <div className="tw-cds-my-space-04">
            <NextPaymentDate
              nextPaymentDate={nextPayment.nextPaymentDate}
              isEstimated={nextPayment.isEstimated}
            />
          </div>
        )}
        <div
          style={{
            height: '18rem',
            overflow: 'auto',
          }}
        >
          <PayrollReceiptsList
            receipts={receipts}
            onViewReceipt={handleViewReceipt}
            onDownloadReceipt={handleDownloadReceipt}
            downloadingReceiptId={downloadingReceiptId}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className="tw-w-full tw-h-full tw-flex tw-flex-col tw-cds-bg-layer-01 tw-rounded-1.6 tw-p-2 tw-overflow-hidden"
    >
      <header className="tw-shrink-0 tw-flex tw-flex-col tw-items-start tw-cds-gap-title-s">
        <h2 className="tw-cds-font-subtitle-01">{t('PAYROLL_WIDGET_TITLE')}</h2>
        {!hasError && !isLoading && (
          <p className="tw-cds-font-body-01 tw-cds-text-subtle">
            {t('PAYROLL_WIDGET_SUBTITLE')}
          </p>
        )}
      </header>

      {renderContent()}

      {receiptToView && (
        <ComplexDialog
          open={!!receiptToView}
          onOpenChange={open => {
            // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
            if (!open) handleCloseViewModal();
          }}
        >
          <ViewReceiptModal
            receipt={receiptToView}
            onClose={handleCloseViewModal}
          />
        </ComplexDialog>
      )}
    </div>
  );
});

PayrollReceiptWidget.displayName = 'PayrollReceiptWidget';
